import { useState } from 'react';
import {
  Edit,
  SimpleShowLayout,
  useCreate,
  useDataProvider,
  useEditController,
  useNotify,
  useRedirect,
  useUpdate,
} from 'react-admin';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { CircularProgress, MenuItem, Select, Typography } from '@mui/material';
import { useGateValue } from '@statsig/react-bindings';

import { useCustomer, usePetPlans, usePolicyListByPetId } from '@pumpkincare/users';

import useBooleanInput from '../../../hooks/useBooleanInput';
import RoutePaths from '../../../routes';
import MessageModal from '../../../shared/components/message-modal';
import { petIsWellnessOnly } from '../../../shared/utils/petUtils';
import AdjustPolicyUtilization from '../adjust-policy-utilization';
import PetDetailsEdit from '../pet-edit-details/pet-edit-details';
import PolicyChangeRequest from '../policy-change-request';
import { PolicyChangeSideBar } from '../policy-change-sidebar';
import {
  addIdToMidtermOptions,
  formatPetEditTitle,
  getPetChangeOptions,
  sortMidtermOptions,
  validateWellnessRenewalChange,
} from '../utils/pet-utils';
import { WellnessChangeSideBar } from '../wellness-change-sidebar';
import WellnessRenewalChange from '../wellness-renewal-change';
import { petEditStyle } from './pet-edit-style';

const WA_START_DATE = new Date('2023-07-14');
const WA_END_DATE = new Date('2024-01-02');

function PetEdit(props) {
  const classes = petEditStyle();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create, { isLoading: isCreateMidtermLoading }] = useCreate();
  const dataProvider = useDataProvider();
  const [createRenewalChange, { isLoading: isCreateRenewalChangeLoading }] =
    useCreate();

  const [
    createWellnessRenewalChange,
    { isLoading: isWellnessCreateRenewalChangeLoading },
  ] = useCreate();

  const { record: petData } = useEditController(props);
  const isAdjustClaimUtilizationEnabled = useGateValue(
    'fer-7646-adjusting-claim-utilization'
  );
  const PET_CHANGE_OPTIONS_RESOLVED = getPetChangeOptions(
    petData,
    isAdjustClaimUtilizationEnabled
  );

  const [petChangeType, setPetChangeType] = useState();
  const [midtermSelectedData, setMidtermData] = useState();
  const [renewalChangeSelectedData, setRenewalChangeSelectedData] = useState();

  const [midtermFormattedData, setMidtermDataFormatted] = useState();
  const [renewalChangeFormattedData, setRenewalChangeDataFormatted] = useState();
  const [wellnessFormattedData, setWellnessFormattedData] = useState();

  const [isLoadingChangeData, setIsLoadingChangeData] = useState(false);
  const [message, setMessage] = useState('');

  const [midtermChangeData, setMidtermChangeData] = useState();
  const [renewalChangeData, setRenewalChangeData] = useState();
  const [tierOptions, setTierOptions] = useState();

  const [isMidtermChangeMessageModalOpen, toggleMidtermChangeMessageModal] =
    useBooleanInput();

  const [update, { isLoading }] = useUpdate();

  const { data: customerData, isLoading: isCustomerDataLoading } = useCustomer(
    petData?.user_id
  );

  const { data: petPolicies } = usePolicyListByPetId(petData?.id);

  const { data: petPlanData } = usePetPlans(petData?.id);

  if (petData) {
    petData.renewal_pending_policy =
      petPolicies &&
      petPolicies.filter(policy => policy.status == 'renew_pending')[0];
    if (petData.renewal_pending_policy)
      petData.renewal_pending_policy.version =
        petPolicies && petData.renewal_pending_policy.program_id.match(/\d\.\d/g)[0];

    petData.renewal_pending_pet_plan =
      petPlanData &&
      petPlanData.filter(pet_plan => pet_plan.status == 'renew_pending')[0];
  }

  const midtermDataOrdered = midtermChangeData
    ? addIdToMidtermOptions(sortMidtermOptions(midtermChangeData))
    : [];

  const renewalChangeDataOrdered = renewalChangeData
    ? addIdToMidtermOptions(sortMidtermOptions(renewalChangeData))
    : [];

  const isMidterm = petChangeType === 'midterm';
  const shouldShowPolicyChangeSideBar =
    petChangeType !== 'details' && (isMidterm || petChangeType === 'renewal');

  const shouldShowWellnessChangeSideBar =
    petChangeType !== 'details' && petChangeType === 'wellness_renewal';

  //Avoid useless warnings due to basePath is being passed into a div
  // eslint-disable-next-line no-unused-vars
  function SanitizedDiv({ basePath, ...props }) {
    return <div style={{ width: '100%' }} {...props} />;
  }

  function handleRatesChange(policy_change) {
    if (petChangeType == 'midterm') {
      setMidtermData(policy_change);
      setMidtermDataFormatted({
        ...petData,
        latest_pet_policy: {
          ...petData.latest_pet_policy,
          raw_rate: policy_change.raw_rate,
          filed_rate: policy_change.filed_rate,
          deductible: policy_change.deductible,
          coinsurance: policy_change.coinsurance,
          annual_limit: policy_change.annual_limit,
        },
      });
    } else {
      setRenewalChangeSelectedData(policy_change);
      setRenewalChangeDataFormatted({
        ...petData,
        renewal_pending_policy: {
          ...petData.renewal_pending_policy,
          raw_rate: policy_change.raw_rate,
          filed_rate: policy_change.filed_rate,
          capped_rate: policy_change.capped_rate,
          deductible: policy_change.deductible,
          coinsurance: policy_change.coinsurance,
          annual_limit: policy_change.annual_limit,
        },
      });
    }
  }

  function handleTierChange(tier) {
    const price = tierOptions?.options.find(option => option.tier === tier)?.price;
    setWellnessFormattedData({
      tier: tier,
      price: price,
      is_annualized: petData.wellness_plans.find(
        plan => plan.status === 'renew_pending'
      )?.is_annualized,
    });
  }

  function handleEditPetDetails(petData, pet_id, breedList) {
    const breed = breedList.find(breed => breed.code === petData?.breed_code);
    const payload = {
      name: petData.name,
      gender: petData?.gender,
      age: petData?.age,
      breed_name: breed.name,
      breed_type: breed.type,
      breed_code: breed.code,
    };

    update(
      RoutePaths.pets,
      { data: payload, id: pet_id },
      {
        onSuccess: () => {
          redirect(`/${RoutePaths.pets}/${pet_id}/show`);
        },
        onError: error => {
          notify(
            `There was an error while updating the pet details: ${error.message}`,
            {
              type: 'error',
            }
          );
        },
      }
    );
  }

  function saveMidtermChanges() {
    create(
      `/${RoutePaths.policies}/${petData.latest_pet_policy.id}/midterm`,
      { data: midtermSelectedData },
      {
        onSuccess: () => {
          redirect(`/${RoutePaths.pets}/${petData.id}/show`);
        },
        onError: error => {
          setMessage(error.message);
          toggleMidtermChangeMessageModal();
        },
      }
    );
  }

  function saveRenewalChange() {
    createRenewalChange(
      `/${RoutePaths.policies}/${petData.renewal_pending_policy.id}/renewal`,
      { data: renewalChangeSelectedData },
      {
        onSuccess: () => {
          redirect(`/${RoutePaths.pets}/${petData.id}/show`);
        },
        onError: error => {
          setMessage(error.message);
          toggleMidtermChangeMessageModal();
        },
      }
    );
  }

  function saveWellnessRenewalChange() {
    createWellnessRenewalChange(
      `/${RoutePaths.wellness}/${
        petData.wellness_plans.find(plan => plan.status === 'renew_pending')?.id
      }/renewal-change`,
      {
        data: {
          tier: wellnessFormattedData?.tier.replace('-', '.'),
          price: parseFloat(wellnessFormattedData?.price).toFixed(2),
        },
      },
      {
        onSuccess: () => {
          redirect(`/${RoutePaths.pets}/${petData.id}/show`);
        },
        onError: error => {
          notify(error.message, { type: 'error' });
        },
      }
    );
  }

  function handleConfirmChanges() {
    switch (petChangeType) {
      case 'midterm':
        saveMidtermChanges();
        break;
      case 'renewal':
        saveRenewalChange();
        break;
      case 'wellness_renewal':
        saveWellnessRenewalChange();
        break;
      default:
      // code block
    }
  }

  function renderWellnessChangeSideBar(
    petData,
    wellnessFormattedData,
    isWellnessCreateRenewalChangeLoading,
    handleConfirmChanges
  ) {
    return petData ? (
      <WellnessChangeSideBar
        petData={petData}
        wellnessFormattedData={wellnessFormattedData}
        loading={isWellnessCreateRenewalChangeLoading}
        onConfirmChanges={handleConfirmChanges}
      />
    ) : (
      <CircularProgress />
    );
  }

  function onTypeChange(changeType) {
    if (changeType === 'renewal' && petData && !petData.renewal_pending_policy) {
      notify(
        'Renewal change can not be completed. This pet is not within the renewal window.',
        { type: 'error' }
      );
      return;
    }

    const wellnessValidation = validateWellnessRenewalChange(petData, changeType);
    if (!wellnessValidation.valid) {
      notify(wellnessValidation.message, { type: 'error' });
      return;
    }

    const effectiveDate = new Date(
      petData?.latest_pet_policy?.policy_effective_date
    );
    const isDateWA = effectiveDate >= WA_START_DATE && effectiveDate <= WA_END_DATE;

    if (
      changeType === 'midterm' &&
      petData?.latest_pet_policy?.program_id?.endsWith('WA') &&
      isDateWA
    ) {
      notify(
        'Program Version Rating Error: Reach out to an Ops team member or Helpliner to get the rates for this pet',
        { type: 'error' }
      );
      return;
    }

    if (changeType === 'details' || changeType === 'adjust_claim_utilization') {
      setPetChangeType(changeType);
      return;
    }

    const currentChangeData =
      changeType === 'midterm' ? midtermDataOrdered : renewalChangeDataOrdered;

    if (isNoChangeOptionsAvailable(changeType, currentChangeData)) {
      notify(`No ${changeType} change options available for this pet.`, {
        type: 'error',
      });
      return;
    }

    const policyId =
      changeType === 'midterm'
        ? petData.latest_pet_policy?.id
        : petData.renewal_pending_policy?.id;

    const wellnessId =
      changeType === 'wellness_renewal'
        ? petData.wellness_plans.find(plan => plan.status === 'renew_pending')?.id
        : null;

    setIsLoadingChangeData(true);

    if (changeType === 'wellness_renewal') {
      dataProvider
        .getOne('wellness', {
          id: `${wellnessId}/wellness-tier-option`,
        })
        .then(({ data }) => {
          setTierOptions(data);
          const renewalWellness = petData.wellness_plans.find(
            plan => plan.status === 'renew_pending'
          );
          setWellnessFormattedData({
            tier: renewalWellness.tier.replace('.', '-'),
            price: data.options.find(
              option => option.tier === renewalWellness.tier.replace('.', '-')
            )?.price,
            is_annualized: renewalWellness?.is_annualized,
          });
        })
        .catch(error => {
          notify(error.message, { type: 'error' });
        })
        .finally(() => {
          setPetChangeType(changeType);
          setIsLoadingChangeData(false);
        });
    } else {
      dataProvider
        .getOne('policies', {
          id: `${policyId}/${changeType}`,
        })
        .then(({ data }) => {
          if (changeType === 'midterm') {
            setMidtermChangeData(data);
          } else {
            setRenewalChangeData(data);
          }
          if (isNoChangeOptionsAvailable(changeType, data)) {
            notify(`No ${changeType} change options available for this pet.`, {
              type: 'error',
            });
          }
        })
        .catch(error => {
          notify(error.message, { type: 'error' });
        })
        .finally(() => {
          setPetChangeType(changeType);
          setIsLoadingChangeData(false);
        });
    }
  }

  function isNoChangeOptionsAvailable(changeType, changeData) {
    return changeData.length && !changeData.some(obj => obj.allowed);
  }

  const midtermContent = (
    <PolicyChangeRequest
      petData={petData}
      changeData={midtermDataOrdered}
      onRatesChange={handleRatesChange}
      changeSelectedData={midtermSelectedData}
      isMidterm={isMidterm}
    />
  );
  const renewalContent = (
    <PolicyChangeRequest
      petData={petData}
      changeData={renewalChangeDataOrdered}
      onRatesChange={handleRatesChange}
      changeSelectedData={renewalChangeSelectedData}
      isMidterm={isMidterm}
    />
  );
  const wellnessRenewalContent = (
    <WellnessRenewalChange
      petData={petData}
      tierOptions={tierOptions?.options}
      onTierChange={handleTierChange}
      changeSelectedData={wellnessFormattedData?.tier}
    />
  );
  const petDetailsContent = (
    <PetDetailsEdit
      petData={petData}
      onEditPetDetails={handleEditPetDetails}
      record={props}
    />
  );
  const adjustClaimUtilizationContent = (
    <AdjustPolicyUtilization petId={petData?.id} />
  );

  function renderEditContent() {
    switch (petChangeType) {
      case 'midterm':
        if (
          Array.isArray(midtermChangeData) &&
          !isNoChangeOptionsAvailable(petChangeType, midtermDataOrdered)
        ) {
          return midtermContent;
        }
        break;
      case 'renewal':
        if (
          Array.isArray(renewalChangeData) &&
          !isNoChangeOptionsAvailable(petChangeType, renewalChangeDataOrdered)
        ) {
          return renewalContent;
        }
        break;
      case 'wellness_renewal':
        if (Array.isArray(tierOptions?.options)) {
          return wellnessRenewalContent;
        }
        break;
      case 'details':
        return petDetailsContent;
      case 'adjust_claim_utilization':
        return adjustClaimUtilizationContent;
      default:
        return null;
    }
    return null;
  }

  return (
    <Edit
      {...props}
      actions={null}
      title={formatPetEditTitle(petData, customerData)}
      classes={{
        root: classes.root,
        noActions: classes.noActions,
      }}
    >
      <SimpleShowLayout>
        <SanitizedDiv>
          <div className={classes.pageContainer}>
            <div className={classes.changeDetailMainContainer}>
              <Link
                to={`/pets/${petData?.id}/show`}
                className={classes.returnToShowPetLink}
              >
                <ArrowBackIosIcon className={classes.arrowBackIcon} />
                <Typography className={classes.returnText}>
                  Show Pet Information
                </Typography>
              </Link>
              {!isCustomerDataLoading ? (
                <>
                  <Typography className={classes.selectOptionLabel}>
                    Change Type
                  </Typography>
                  <Select
                    style={{ height: '40px', marginBottom: '32px' }}
                    variant='outlined'
                    data-testid='select-type'
                    fullWidth
                    value={petChangeType}
                    onChange={e => onTypeChange(e.target.value)}
                  >
                    {PET_CHANGE_OPTIONS_RESOLVED.filter(change =>
                      petIsWellnessOnly(petData)
                        ? change.id === 'details' || change.id === 'wellness_renewal'
                        : true
                    ).map(change => (
                      <MenuItem key={change.id} value={change.id}>
                        {change.name}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              ) : null}
              {isLoading || isLoadingChangeData ? (
                <CircularProgress />
              ) : (
                renderEditContent()
              )}
            </div>
            <div className={classes.changeDetailSideBar}>
              {shouldShowPolicyChangeSideBar ? (
                petData ? (
                  <>
                    <PolicyChangeSideBar
                      petData={petData}
                      policyChangeData={
                        isMidterm ? midtermSelectedData : renewalChangeSelectedData
                      }
                      midtermFormattedData={
                        isMidterm ? midtermFormattedData : renewalChangeFormattedData
                      }
                      loading={
                        isMidterm
                          ? !!isCreateMidtermLoading
                          : !!isCreateRenewalChangeLoading
                      }
                      onConfirmChanges={handleConfirmChanges}
                      isMidterm={isMidterm}
                    />

                    <MessageModal
                      onClose={toggleMidtermChangeMessageModal}
                      isOpen={isMidtermChangeMessageModalOpen}
                      title='The change you are trying to make cannot be completed.'
                      type='error'
                      buttonTitle='Close'
                      message={message}
                      onClick={toggleMidtermChangeMessageModal}
                    />
                  </>
                ) : (
                  <CircularProgress />
                )
              ) : null}

              {shouldShowWellnessChangeSideBar
                ? renderWellnessChangeSideBar(
                    petData,
                    wellnessFormattedData,
                    isWellnessCreateRenewalChangeLoading,
                    handleConfirmChanges
                  )
                : null}
            </div>
          </div>
        </SanitizedDiv>
      </SimpleShowLayout>
    </Edit>
  );
}

export default PetEdit;
